import { MenuItem } from 'primeng/api';
import { ConfirmOptions } from '../confirm-dialog/confirm.signals';

export const ALL_TABLE_ACTION_CONTEXTS = [
  'row',
  'contextMenu',
  'menu',
] as const;

export type TableActionContext = 'row' | 'contextMenu' | 'menu' | 'default';

interface BaseTableActionSchema<Row> {
  label: string;
  routerLink: any[] | string;
  queryParams: {
    [k: string]: any;
  };
  visible: boolean;
  color: 'primary' | 'secondary' | 'danger' | 'warn' | 'success';
  icon: string;
  iconLabel: string;
  styleClass: string;
  disabled: boolean;
  showRowLabel: boolean;
  context: TableActionContext | TableActionContext[] | 'all';
  command: (this: void, row: Row) => void;
}

export interface BulkActionCtx<Row> {
  rows: Row[];
  clearSelection: () => void;
}

export interface ProcessedBulkActionCtx<Row> extends BulkActionCtx<Row> {
  processed: { row: Row; result: any }[];
  errors: { row: Row; error: any }[];
}

/**
 * A standalone action that can be executed on multiple selected rows one-by-one.
 * Configure via `TableFullConfig.bulkActions`. Requires `dataKey` to be set on the table.
 */
export interface BulkAction<Row> {
  label: string;
  icon?: string;
  color?: 'primary' | 'secondary' | 'danger' | 'warn' | 'success';
  styleClass?: string;
  disabled?: boolean;
  visible?: boolean;
  stopOnError?: boolean;
  confirm?: (
    this: void,
    ctx: BulkActionCtx<Row>,
  ) => Omit<ConfirmOptions, 'accept' | 'reject'>;
  command: (this: void, row: Row) => any | Promise<any>;
  then?: (this: void, ctx: ProcessedBulkActionCtx<Row>) => void | Promise<any>;
  catch?: (
    this: void,
    error: any,
    ctx: ProcessedBulkActionCtx<Row>,
  ) => void | Promise<any>;
  finally?: (
    this: void,
    ctx: ProcessedBulkActionCtx<Row>,
  ) => void | Promise<any>;
}

type WithRow<Schema, Row> = {
  [P in keyof Schema]: Schema[P] extends (...args: any[]) => any
    ? Schema[P]
    : Schema[P] | ((this: void, row: Row) => Schema[P] | undefined);
};

type WithFunctionWrapper<Schema> = {
  [P in keyof Schema]: Schema[P] extends (...args: any[]) => any
    ? () => Schema[P]
    : Schema[P];
};

export interface TableAction<Row>
  extends Partial<WithRow<BaseTableActionSchema<Row>, Row>> {
  context: TableActionContext | TableActionContext[] | 'all';
}

export type ProcessedAction<Row> = Partial<
  WithFunctionWrapper<BaseTableActionSchema<Row>>
>;

export type ActionWithoutContext<T> = Omit<TableAction<T>, 'context'>;

export interface CategorizedActions<T> {
  row?: ActionWithoutContext<T>[];
  menu?: ActionWithoutContext<T>[];
  contextMenu?: ActionWithoutContext<T>[];
  default?: ActionWithoutContext<T>;
}

export const processAction = <T>(
  action: ActionWithoutContext<T> | undefined,
  row: T,
): ProcessedAction<T> | undefined => {
  if (!action) {
    return action;
  }

  const functionLikes: (keyof ActionWithoutContext<T>)[] = ['command'];

  const processed = Object.fromEntries(
    Object.entries(action).map(([k, v]) => {
      // if the value is a function, we need to call it with the row
      if (typeof v === 'function') {
        // we need to wrap the function inside another function to make sure it's not called immediately
        if (functionLikes.includes(k as keyof ActionWithoutContext<T>)) {
          return [k, () => v(row)];
        }

        return [k, v(row)];
      }

      return [k, v];
    }),
  ) as ProcessedAction<T>;

  if (processed.visible === false) {
    return undefined;
  }

  return processed;
};

export const processRowActions = <T>(
  row: T,
  actions: CategorizedActions<T>,
) => {
  return (actions?.row
    ?.map((action) => processAction(action, row))
    ?.filter((action) => !!action) ?? []) as ProcessedAction<T>[];
};

export const processMenuActions = <T>(
  row: T,
  actions: CategorizedActions<T>,
) => {
  return (
    actions?.menu
      ?.map((action) => processAction(action, row))
      ?.filter((action) => !!action)
      ?.map((action) => toMenuItem(action!, row)) ?? []
  );
};

export const processContextMenuActions = <T>(
  row: T,
  actions: CategorizedActions<T>,
) => {
  const items =
    actions.contextMenu
      ?.map((action) => processAction(action, row))
      ?.filter((action) => !!action)
      ?.map((action) => toMenuItem(action!, row)) ?? [];

  if (items.length === 0) {
    return undefined;
  }

  return items;
};

export const toMenuItem = <T>(action: ProcessedAction<T>, row: T): MenuItem => {
  return action;
};

export const processDefaultAction = <T>(
  row: T,
  actions: CategorizedActions<T>,
) => {
  return processAction(actions.default, row);
};

export const buildCategorizedActions = <T>(
  actions: TableAction<T>[],
): CategorizedActions<T> => {
  const categorized: CategorizedActions<T> = Object.fromEntries(
    Object.entries(
      actions?.reduce((acc, action) => {
        const contexts: TableActionContext[] = [];

        if (!Array.isArray(action.context)) {
          if (action.context === 'all') {
            contexts.push(...ALL_TABLE_ACTION_CONTEXTS);
          } else {
            contexts.push(action.context);
          }
        } else {
          contexts.push(...action.context);
        }

        // add all actions from row to menu also
        if (contexts.includes('row') && !contexts.includes('menu')) {
          contexts.push('menu');
        }

        contexts.forEach((ctx) => {
          if (ctx === 'default') {
            if (acc[ctx]) {
              throw new Error(`Multiple default actions are not supported`);
            }

            acc['default'] = action;
          } else {
            if (!acc[ctx] || !Array.isArray(acc[ctx])) {
              acc[ctx] = [];
            }

            acc[ctx]?.push(action);
          }
        });

        return acc;
      }, {} as CategorizedActions<T>) ?? {},
    ),
  );

  return categorized as CategorizedActions<T>;
};
